import React, { useEffect, useRef, useState } from 'react';
import { LiveCodexUsagePanel, liveUsageMoney } from './LiveCodexUsagePanel.js';
import { TenantConnectionsPanel } from './TenantConnectionsPanel.js';
import { ensureLiveSession, readLiveEvents, mergeLiveEvents, sendLiveMessage, stopLiveSession,
  topUpLiveSession, dollarsToMicros, LiveBudgetRefused, type LiveEvent, type LiveSessionView } from '../../lib/live-codex.js';

const money = liveUsageMoney;

/** Track B has one conversation engine. An unavailable capability stays unavailable;
 * it cannot silently fall back to the old chat model or a blind Codex run. */
export function LiveCodexChat({ chatSessionId, syncId }: { chatSessionId: string; syncId: string }): React.ReactElement {
  const [session, setSession] = useState<LiveSessionView | null>(null);
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [draft, setDraft] = useState(''); const [notice, setNotice] = useState('Opening your Codex session…');
  const [busy, setBusy] = useState(false); const [topUp, setTopUp] = useState('');
  const [pending, setPending] = useState<{ key: string; text: string } | null>(null);
  const [budgetConsent, setBudgetConsent] = useState<{ key: string; amount: number } | null>(null);
  const generation = useRef(0);
  const receivedEvents = useRef<LiveEvent[]>([]);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const token = ++generation.current; const controller = new AbortController(); let timer: ReturnType<typeof setTimeout> | undefined;
    receivedEvents.current = [];
    setSession(null); setEvents([]); setPending(null); setBudgetConsent(null); setDraft(''); setBusy(false);
    setNotice('Opening your Codex session…');
    let after = '0';
    const poll = async (id: string): Promise<void> => {
      try {
        const result = await readLiveEvents(chatSessionId, id, after, controller.signal);
        if (generation.current !== token || controller.signal.aborted) return;
        // Validate conflicts inside this try, before advancing the durable cursor.
        const merged = mergeLiveEvents(receivedEvents.current, result.events);
        receivedEvents.current = merged; setEvents(merged); after = result.cursor;
        setSession(result.session);
        setNotice((prior: string) => prior.startsWith('Connection interrupted.') ? 'Connected to your Codex session.' : prior);
      } catch {
        if (generation.current !== token || controller.signal.aborted) return;
        setNotice('Connection interrupted. Your Codex session is preserved; reconnecting…');
      }
      if (!controller.signal.aborted) timer = setTimeout(() => { void poll(id); }, 1000);
    };
    void ensureLiveSession(chatSessionId, syncId, controller.signal).then((value: LiveSessionView) => {
      if (generation.current !== token || controller.signal.aborted) return;
      setSession(value); setNotice('Codex is working through your findings, P0 first. You can ask questions here.');
      void poll(value.id);
    }).catch(() => {
      if (generation.current === token && !controller.signal.aborted) setNotice('Codex is not available for this Sync yet. Your findings are preserved.');
    });
    return () => { controller.abort(); if (timer) clearTimeout(timer); };
  }, [chatSessionId, syncId]);
  useEffect(() => { list.current?.scrollTo({ top: list.current.scrollHeight, behavior: 'smooth' }); }, [events]);

  const send = async (): Promise<void> => {
    if (!session || busy || (!pending && !draft.trim())) return;
    const current = pending ?? { key: crypto.randomUUID(), text: draft.trim() };
    const token = generation.current; setBusy(true); setPending(current);
    try {
      const result = await sendLiveMessage(chatSessionId, session.id, current.key, current.text);
      if (token !== generation.current) return;
      if (result.uncertain) setNotice('Your message may be running. Check the same message before sending another.');
      else if (['claimed','existing'].includes(result.admission)) { setPending(null); setDraft(''); setNotice('Message received by Codex.'); }
      else if (result.admission === 'budget_exhausted') setNotice(session.maximumTopUpMicros > 0
        ? 'This Sync’s budget is used or reserved. Approve more budget before continuing.'
        : 'This Sync’s approved budget is used or reserved. No additional spending is enabled.');
      else if (result.admission === 'busy') setNotice('Codex is working on the current turn. Your message is preserved here.');
      else setNotice('This message conflicts with an earlier request. Reload the conversation before continuing.');
    } catch { if (token === generation.current) setNotice('Message delivery is unconfirmed. Check the same message to avoid starting it twice.'); }
    finally { if (token === generation.current) setBusy(false); }
  };
  const stop = async (): Promise<void> => {
    if (!session || busy) return; const token = generation.current; setBusy(true);
    try { await stopLiveSession(chatSessionId, session.id);
      if (token === generation.current) setNotice('Stop requested. Waiting for execution and usage confirmation.');
    } catch { if (token === generation.current) setNotice('Stop could not be confirmed. Please check again.'); }
    finally { if (token === generation.current) setBusy(false); }
  };
  const addBudget = async (): Promise<void> => {
    if (!session || busy || session.maximumTopUpMicros <= 0) return;
    const amount = dollarsToMicros(topUp); if (amount === null) return;
    const consent = budgetConsent?.amount === amount ? budgetConsent : { key: crypto.randomUUID(), amount };
    const token = generation.current; setBudgetConsent(consent); setBusy(true);
    try {
      const updated = await topUpLiveSession(chatSessionId, session.id, consent.key, consent.amount);
      if (token === generation.current) { setSession(updated); setBudgetConsent(null); setTopUp(''); setNotice('Additional budget approved. You can continue this conversation.'); }
    } catch (error: unknown) {
      if (token === generation.current) {
        if (!budgetConsent && error instanceof LiveBudgetRefused) {
          setBudgetConsent(null); setNotice('This amount exceeds your plan’s limit. Enter a lower amount.');
        } else setNotice('Budget approval could not be confirmed. Retrying this amount will check the same approval.');
      }
    }
    finally { if (token === generation.current) setBusy(false); }
  };
  const additional = dollarsToMicros(topUp);
  return <section aria-label="Live Codex session" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 450, gap: 12, padding: 16 }}>
    <header><h2>Codex</h2>{session && <p>{session.repository} · {session.findingCount} findings · {session.branch}</p>}
      {session && <p>Budget used {money(session.spentMicros)} · Reserved {money(session.reservedMicros)} · Remaining {money(session.budgetLimitMicros - session.spentMicros - session.reservedMicros)} · Limit {money(session.budgetLimitMicros)}</p>}
      {session && <small>Session allowance includes refunded usage and whole-credit rounding.</small>}
      {session && session.maximumTopUpMicros === 0 && <p>Fixed {money(session.budgetLimitMicros)} cap for this Sync, including resumed turns. No top-ups.</p>}
    </header>
    <TenantConnectionsPanel key={chatSessionId} chatSessionId={chatSessionId} />
    {session && <LiveCodexUsagePanel chatSessionId={chatSessionId} sessionId={session.id} />}
    <p role="status" aria-live="polite">{notice}</p>
    <div ref={list} role="log" aria-label="Codex conversation" style={{ flex: 1, overflowY: 'auto' }}>
      {events.map((event: LiveEvent) => <article key={event.cursor} style={{ padding: '8px 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        <strong>{event.kind === 'user_text' ? 'You' : event.kind === 'assistant_text' ? 'Codex' : 'Progress'}</strong>
        <div>{event.text}</div>
      </article>)}
    </div>
    <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => { event.preventDefault(); void send(); }}>
      <label htmlFor="live-codex-message">Message Codex</label>
      <textarea id="live-codex-message" value={draft} maxLength={64000} disabled={!session || busy || !!pending}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(event.target.value)} style={{ width: '100%' }} />
      <button type="submit" disabled={!session || busy || (!pending && !draft.trim())}>{pending ? 'Check same message' : 'Send'}</button>
      <button type="button" disabled={!session || busy} onClick={() => { void stop(); }}>Stop</button>
    </form>
    {session && session.maximumTopUpMicros > 0 && <form aria-label="Approve additional session budget" onSubmit={(event: React.FormEvent<HTMLFormElement>) => { event.preventDefault(); void addBudget(); }}>
      <label htmlFor="live-codex-budget">Additional budget in USD</label>
      <input id="live-codex-budget" inputMode="decimal" value={topUp} disabled={busy || !!budgetConsent}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTopUp(event.target.value)} />
      <button type="submit" disabled={busy || additional === null}>{additional === null ? 'Approve additional budget' : `Approve ${money(additional)} more`}</button>
    </form>}
    <small>Codex opens a pull request for your approval. A finding closes after merge and a confirming Sync.</small>
  </section>;
}
